// @ts-strict-ignore
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { AppPlatform, AppStoreType, ReleaseType, UpcomingReleaseSummary } from '../../types';
import { RollbackTag } from '@/components/appHome/RollbackTag';
import { AppComponentProps, AppRouteComponentMatchParams } from '../../typescriptHelpers';
import { FeatureCompletionWidget } from '@/components/appHome/widgets/FeatureCompletionWidget';
import { ReleaseDescriptionWidget } from '@/components/appHome/widgets/ReleaseDescriptionWidget';
import { ReleasePilotWidget } from '@/components/appHome/widgets/ReleasePilotWidget';
import { ReleaseStepsWidget } from '@/components/appHome/widgets/ReleaseStepsWidget';
import { ReleaseTimelineWidget } from '@/components/appHome/widgets/ReleaseTimelineWidget';
import { TargetDatesWidget } from '@/components/appHome/widgets/TargetDatesWidget';
import { AppHomeCard } from '@/components/appHome/AppHomeCard';
import Tag from 'antd/lib/tag';
import { antGrey, titleStyle } from '@/components/styles/globalStyles';
import { Divider } from 'antd/lib';
import { useSelector } from 'react-redux';
import {
    selectReleaseStepsBlockingConfigForReleaseType,
    selectShouldHideNonBlockingSteps,
    selectIsWorkflowsCIStepEnabled,
} from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';

interface UpcomingReleaseSummaryProps extends AppComponentProps {
    summary: UpcomingReleaseSummary;
    appStoreType: AppStoreType;
    platform: AppPlatform;
}

export const UpcomingReleaseComponent: React.FC<UpcomingReleaseSummaryProps> = (props) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();

    const shouldHideNonBlockingSteps = useSelector((state: RootState) =>
        selectShouldHideNonBlockingSteps(state, appIdParam),
    );

    const nonBlockingStepsConfigMap = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, appIdParam),
    );

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, appIdParam),
    );

    return (
        <AppHomeCard
            appId={appIdParam}
            orgId={orgIdParam}
            releaseId={props.summary.releaseId}
            id={props.summary.id}
            displayableVersion={props.summary.displayableVersion}
            releaseName={props.summary.releaseName}
            shouldStartCollapsed
            headerComponent={
                <>
                    <Tag
                        style={{
                            ...titleStyle,
                            fontSize: 11,
                            lineHeight: '22px',
                        }}
                        color={antGrey.grey3}
                    >
                        Upcoming
                    </Tag>
                    {props.summary.type === ReleaseType.Rollback && <RollbackTag />}
                </>
            }
            contentComponent={
                <>
                    <ReleaseDescriptionWidget description={props.summary.planningSummary} />
                    <ReleaseTimelineWidget
                        appId={appIdParam}
                        orgId={orgIdParam}
                        releaseId={props.summary.releaseId}
                        events={props.summary.events}
                        maxHeight={290}
                        releaseType={props.summary.type}
                    />
                </>
            }
            sideComponent={
                <>
                    <TargetDatesWidget appId={appIdParam} summary={props.summary} />
                    <FeatureCompletionWidget featuresStep={props.summary.featuresStep} />
                    <Divider
                        style={{
                            margin: '16px 0',
                        }}
                    />
                    <ReleaseStepsWidget
                        steps={props.summary.steps}
                        platform={props.platform}
                        phasedRelease={props.summary.phasedRelease}
                        appStoreType={props.appStoreType}
                        shouldHideNonBlockingSteps={shouldHideNonBlockingSteps}
                        nonBlockingStepsConfigMap={nonBlockingStepsConfigMap}
                        releaseType={props.summary.type}
                        isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                    />
                    <Divider
                        style={{
                            margin: '16px 0',
                        }}
                    />
                    <ReleasePilotWidget
                        appId={appIdParam}
                        releasePilotId={props.summary.releasePilotId}
                        onReleaseOverview={false}
                    />
                </>
            }
        />
    );
};
